<template>
  <div
    id="testimonials-plans"
    class="container testimonials-plans w-100 mt-5 mb-5"
    :class="{ alternative: alternativeLayout }"
    :style="bgColor"
  >
    <h2 v-if="title" class="title-testimonials font-weight-bold text-center">
      {{ title }}
    </h2>
    <p v-if="alternativeLayout && subtitle" class="subtitle-testimonials">{{ subtitle }}</p>
    <div
      class="carousel-container position-relative"
      :class="[
        `${hooperLoaded ? 'visible' : 'invisible max-h-25'}`,
        { 'overflow-hidden p-3': alternativeLayout }
      ]"
    >
      <Hooper
        ref="carousel"
        :settings="hooperSettings"
        class="hooper-style h-auto"
        :class="{ 'text-center': !alternativeLayout }"
        alternative-layout
        @slide="updateCarousel"
        @loaded="handleHooperLoaded"
      >
        <Slide v-for="(slide, index) in slides" :key="index" class="opacity">
          <div class="slide-item">
            <span v-if="alternativeLayout" class="quotation-marks">
              <Icon icon="quotes-purple" :width="35" :height="35" />
            </span>
            <p v-if="isDefaultTestimonial" class="testimony">{{ $t(slide.testimony) }}</p>
            <prismic-rich-text
              v-else
              :field="slide.testimony"
              class="testimony"
            ></prismic-rich-text>
            <div class="testimony-info">
              <img
                v-if="slide.photo"
                :src="slide.photo"
                :alt="slide.name"
                class="photo"
                loading="lazy"
              />
              <div class="testimony-texts">
                <span v-if="slide.name" class="name" :class="{ 'no-site': !slide.site }">{{
                  slide.name
                }}</span>
                <span v-if="slide.site" class="text-site">{{ slide.site }}</span>
              </div>
            </div>
          </div>
        </Slide>
      </Hooper>
      <div class="arrow-position left" @click.prevent="slidePrev">
        <button v-if="!alternativeLayout" class="hooper-prev-custom">
          <Icon class="invert" icon="arrow" color="white" :width="iconSize" :height="iconSize" />
        </button>
        <ButtonRounded v-else class="alternative-arrow"
          ><Icon icon="arrow-left" color="--color-primary" :height="15" class="rounded-tick" />
        </ButtonRounded>
      </div>
      <div v-if="!alternativeLayout" class="position-absolute w-100 d-flex align-items-center">
        <ol class="hooper-indicators">
          <li v-for="(slide, index) in slides" :key="index">
            <button
              type="button"
              class="hooper-indicator"
              :class="index + 1 === carouselData ? 'is-active' : ''"
              @click="goToSlide(index + 1)"
            >
              <span class="hooper-sr-only">item {{ index + 1 }}</span>
            </button>
          </li>
        </ol>
      </div>
      <div class="arrow-position right" @click.prevent="slideNext">
        <button v-if="!alternativeLayout" class="hooper-next-custom">
          <Icon icon="arrow" color="white" :width="iconSize" :height="iconSize" />
        </button>
        <ButtonRounded v-else :height="2" class="alternative-arrow"
          ><Icon icon="arrow-right" color="--color-primary" :height="15" class="rounded-tick" />
        </ButtonRounded>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Hooper, Slide } from 'hooper';
import 'hooper/dist/hooper.css';
import Icon from 'UI/Icon';
import ButtonRounded from 'Components/Plans/PlansCompareV2/ButtonRounded.vue';

export default {
  name: 'Testimonials',
  components: {
    Hooper,
    Slide,
    ButtonRounded,
    Icon
  },
  props: {
    indicatorsColor: {
      type: String,
      required: false,
      default: ''
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    subtitle: {
      type: String,
      required: false,
      default: ''
    },
    testimonials: {
      type: Array,
      required: false,
      default: () => []
    },
    alternativeLayout: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      carouselData: 1,
      hooperSettings: {
        itemsToShow: this.alternativeLayout ? 1.2 : 1,
        centerMode: true,
        infiniteScroll: true,
        autoPlay: true,
        wheelControl: false,
        breakpoints: {
          769: {
            itemsToShow: this.alternativeLayout ? 1 : 2
          },
          1200: {
            itemsToShow: this.alternativeLayout ? 1 : 3
          }
        }
      },
      iconSize: 14,
      hooperLoaded: false
    };
  },
  computed: {
    ...mapGetters('config', ['getPageBackgroundColor']),
    ...mapGetters('experience', ['getExperienceColor']),
    bgColor() {
      const backgroundColor = this.getPageBackgroundColor;
      return {
        '--bg-color': this.indicatorsColor !== '' ? this.indicatorsColor : this.getExperienceColor,
        '--background-color': backgroundColor ? backgroundColor : '#ffffff'
      };
    },
    slides() {
      return this.testimonials.length > 0 ? this.testimonials : this.$g('TESTIMONIALS');
    },
    isDefaultTestimonial() {
      return this.testimonials.length === 0;
    }
  },
  watch: {
    carouselData() {
      this.$refs.carousel.slideTo(this.carouselData);
    }
  },
  methods: {
    handleHooperLoaded() {
      this.hooperLoaded = !this.hooperLoaded;
    },
    slidePrev() {
      this.$refs.carousel.slidePrev();
    },
    slideNext() {
      this.$refs.carousel.slideNext();
    },
    updateCarousel(payload) {
      this.carouselData = payload.currentSlide;
    },
    goToSlide(slide) {
      this.carouselData = slide;
    }
  }
};
</script>

<style lang="scss">
#testimonials-plans {
  .hooper-list {
    .is-active {
      opacity: 0.3;
      transition: opacity 0.3s;
    }
    .is-current {
      opacity: 1;
    }
  }
  &.alternative {
    .hooper-slide {
      height: auto;
    }
    .hooper-list {
      overflow: inherit;
    }
  }
}
</style>

<style lang="scss" scoped>
.vx-custom-background {
  #testimonials-plans {
    --title-color: white;
    --text-color: white;
    --text-name-color: white;
  }
}
#testimonials-plans {
  display: flow-root;
  --title-color: #{$lightBlue};
  --color-primary: #9b86f7;

  .testimony-texts {
    flex-direction: column;
    display: flex;
  }
  .hooper-slide {
    padding: 0 10px;
  }
  &.alternative {
    .arrow-position {
      top: calc(50% - 25px);
      &.left {
        left: calc(2% - 10px);
        .svg-icon-container {
          right: 0px;
        }
      }
      &.right {
        right: calc(2% - 10px);
        .svg-icon-container {
          left: 0px;
        }
      }
      .svg-icon-container {
        position: relative;
      }
    }
    .alternative-arrow {
      height: auto;
      width: 40px;
      background-color: var(--background-color);
    }
    .title-testimonials {
      margin: 25px 0;
      color: var(--title-color);
    }
    .hooper-style {
      margin-top: 25px;
    }
    .slide-item {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: var(--background-color) 0% 0% no-repeat padding-box;
      box-shadow: 0px 3px 10px rgba(0, 0, 0, 0.2);
      border: 2px solid var(--purple);
      border-radius: 24px;
      padding: 20px 70px 40px;
      margin: 0 auto;
      width: 87%;
      @include xl {
        padding: 10px 30px 25px;
      }
      @include sm {
        margin: 15px;
        width: calc(100% - 30px);
        padding: 10px 20px;
      }
      .photo {
        width: 46px;
        height: 46px;
      }
      .quotation-marks {
        color: var(--purple);
        font-size: 40px;
        margin: 10px 0 0;
      }
      .testimony {
        color: var(--text-color);
        text-align: left;
        font-size: 18px;
        margin: 10px 0 20px 0;
      }
      .testimony-info {
        display: flex;
        .testimony-texts {
          margin-left: 10px;
          .name {
            margin: 0;
            text-align: left;
            font-size: 15px;
            color: var(--text-name-color);
            &.no-site {
              margin-top: 12px;
            }
          }
          .text-site {
            font-size: 13px;
            color: #c5c5c5;
            @include sm {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              text-align: left;
            }
          }
        }
        @include sm {
          flex-direction: column;
          .testimony-texts {
            margin-left: 0;
          }
        }
      }
    }
    @include md {
      .arrow-position {
        display: none;
      }
      .hooper-slide {
        padding: 0;
      }
    }
  }
}
#testimonials-plans {
  --text-color: #{$strongGray};
  --text-name-color: #2e2e2e;
  .title-testimonials {
    color: var(--text-color);
    font-size: 20px;
    margin: 48px 0;
  }
  .subtitle-testimonials {
    font-size: 33px;
    font-weight: 500;
    color: var(--text-name-color);

    text-align: center;
    margin: 0;
  }
}
#testimonials-plans > .carousel-container {
  background: var(--background-color) 0% 0% no-repeat padding-box;
  margin-bottom: 100px;
}
.hooper-style {
  margin-top: 100px;
}
.hooper-indicator {
  border-radius: 200%;
  width: 10px;
  height: 10px;
  background-color: #dadada;
}
.hooper-indicators {
  margin: auto;
}
.hooper-indicator:hover,
.hooper-indicator.is-active {
  background-color: var(--bg-color);
}
p.testimony {
  font-size: 15px;
  margin-bottom: 70px;
}
.testimony {
  color: var(--text-color);
}
.photo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
}
.name {
  color: var(--text-color);
  font-size: 18px;
  margin: 15px 0;
}
.text-site {
  color: #999999;
  font-size: 13px;
}
.arrow-position {
  position: absolute;
  top: 50%;
  .svg-icon-container {
    position: absolute;
  }
  &.left {
    left: 0;
    .svg-icon-container {
      right: 11px;
    }
  }
  &.right {
    right: 0;
    .svg-icon-container {
      left: 11px;
    }
  }
}
.hooper-next-custom,
.hooper-prev-custom {
  border: none;
  padding: 0;
  transform: translateY(-50%);
  cursor: pointer;
  background-color: var(--bg-color);
  border-radius: 200%;
  height: 35px;
  width: 35px;
  font-size: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.max-h-25 {
  max-height: 25rem;
}
</style>
